import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Zap, Calendar, ExternalLink, Loader2, Sparkles, Shield } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return "—"; }
};
const euro = (n) => `€ ${Number(n || 0).toFixed(2).replace(".", ",")}`;

const STATUS_LABEL = {
  free: "Free",
  active: "Attivo",
  trialing: "In prova",
  past_due: "Pagamento in ritardo",
  canceled: "Cancellato",
  unpaid: "Non pagato",
  incomplete: "Incompleto",
};

export default function PlanTab() {
  const { user, isPro, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([
          api.get("/plan/status"),
          api.get("/plan/config"),
        ]);
        setStatus(s.data);
        setConfig(c.data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (!loading && window.location.hash === "#abbonamento") {
      document.getElementById("abbonamento")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [loading]);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore apertura portale");
      setPortalLoading(false);
    }
  };

  if (loading || !status) return null;

  const badge = isPro ? "Pro" : "Free";
  const StatusIcon = isPro ? Crown : Zap;
  const label = STATUS_LABEL[status.status] || status.status;

  return (
    <motion.section
      initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
      id="abbonamento"
      data-testid="plan-tab"
      className="scroll-mt-24 rounded-2xl border border-[var(--border-c)] bg-[var(--bg-2)] p-6 md:p-8 shadow-[0_2px_12px_rgba(45,41,38,0.04)]"
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--fg-3)] font-sans font-semibold">
            Piano attuale
          </div>
          <h3 className="mt-2 font-serif text-3xl md:text-4xl text-[var(--fg-1)] tracking-tight flex items-center gap-3">
            <StatusIcon size={22} strokeWidth={2} className={isPro ? "text-[#00F2F4]" : "text-[var(--fg-2)]"} />
            RecenUp <span className={isPro ? "italic text-[#00F2F4]" : "italic"}>{badge}</span>
          </h3>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-[var(--fg-3)]">
            <span data-testid="plan-tab-status" className="inline-flex items-center gap-1 rounded-full bg-[var(--bg-3)] px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em] font-sans">
              <Shield size={10} /> {label}
            </span>
            {status.grandfathered && (
              <span data-testid="plan-tab-grandfathered" className="inline-flex items-center gap-1 rounded-full bg-[#E9A94E]/15 text-[#B57F1F] px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em] font-sans">
                Legacy · limiti storici mantenuti
              </span>
            )}
            {status.cancel_at_period_end && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#C6423B]/12 text-[#C6423B] px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em] font-sans">
                Cancellazione programmata
              </span>
            )}
          </div>
        </div>

        <div className="text-right">
          {isPro ? (
            <>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] font-sans">Prossimo rinnovo</div>
              <div className="mt-1 font-serif text-2xl text-[var(--fg-1)] leading-none flex items-center gap-2 justify-end" data-testid="plan-tab-next-renewal">
                <Calendar size={16} className="text-[var(--fg-3)]" />
                {fmtDate(status.current_period_end)}
              </div>
              <div className="mt-1 text-xs text-[var(--fg-3)]">{euro(config?.pro_price_eur)}/mese</div>
            </>
          ) : (
            <>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] font-sans">Prezzo Pro</div>
              <div className="mt-1 font-serif text-3xl text-[var(--fg-1)] leading-none" data-testid="plan-tab-pro-price">
                {euro(config?.pro_price_eur)}<span className="text-sm text-[var(--fg-3)]"> /mese</span>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="mt-8 pt-6 border-t border-[var(--border-c)] flex flex-wrap gap-3">
        {isPro ? (
          <button
            onClick={openPortal}
            disabled={portalLoading}
            data-testid="plan-tab-manage-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#00F2F4] hover:bg-[#00D6D8] text-[#0A0A0A] px-5 py-2.5 text-sm font-sans font-semibold disabled:opacity-60"
            style={{ transition: "background-color 0.2s ease" }}
          >
            {portalLoading ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
            {portalLoading ? "Apertura portale…" : "Gestisci abbonamento"}
          </button>
        ) : (
          <Link
            to="/pricing"
            data-testid="plan-tab-upgrade-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#00F2F4] hover:bg-[#00D6D8] text-[#0A0A0A] px-5 py-2.5 text-sm font-sans font-semibold"
            style={{ transition: "background-color 0.2s ease" }}
          >
            <Sparkles size={14} strokeWidth={2.2} />
            Passa a Pro · {euro(config?.pro_price_eur)}/mese
          </Link>
        )}
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 rounded-full border border-[var(--border-c)] hover:border-[var(--fg-2)] text-[var(--fg-1)] px-5 py-2.5 text-sm font-sans"
          style={{ transition: "border-color 0.2s ease" }}
        >
          Confronta piani
        </Link>
        <button
          onClick={refresh}
          className="text-xs uppercase tracking-[0.2em] text-[var(--fg-3)] hover:text-[var(--fg-1)] self-center"
          style={{ transition: "color 0.2s ease" }}
        >
          Aggiorna stato
        </button>
      </div>
    </motion.section>
  );
}
